/**
 * Number and time formatting for the admin views.
 *
 * Grouping uses commas ("9,812") so operator figures read the same on every
 * machine; credits are plain two-decimal numbers — never a currency symbol
 * (constitution VI, contract admin-ui "Copy rules"). Timestamps are UTC, the
 * same timezone the daily figures use.
 */

const countFormatter = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 });
const creditFormatter = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const percentFormatter = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const finite = (value: unknown) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

export const formatCount = (value: number | null | undefined) => countFormatter.format(finite(value));

/** 0.3125 → "31.3%". Null/undefined → "—". */
export const formatPercent = (ratio: number | null | undefined) =>
  ratio === null || ratio === undefined || !Number.isFinite(Number(ratio)) ? '—' : `${percentFormatter.format(Number(ratio) * 100)}%`;

/** "512300.00" → "512,300.00" (play credits; no symbol). */
export const formatCredits = (value: string | number | null | undefined) => creditFormatter.format(finite(value));

/** Signed credits with a real minus sign: "+35.00", "−131.60", "0.00". */
export const formatSignedCredits = (value: string | number | null | undefined) => {
  const number = finite(value);
  if (number === 0) return creditFormatter.format(0);
  return `${number > 0 ? '+' : '−'}${creditFormatter.format(Math.abs(number))}`;
};

const pad = (value: number) => String(value).padStart(2, '0');

/** "2026-09-26T08:00:00Z" → "2026-09-26 08:00 UTC". */
export const formatUtc = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())} UTC`;
};

/** Local wall-clock time of the viewer, "HH:MM:SS". */
export const formatClock = (epochMs: number) => {
  const date = new Date(epochMs);
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

/** Elapsed milliseconds → "4 s ago", "3 min ago", "2 h ago". */
export const formatAgo = (elapsedMs: number) => {
  const seconds = Math.max(0, Math.floor(elapsedMs / 1000));
  if (seconds < 60) return `${seconds} s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
};

const PLATFORM_LABEL: Record<string, string> = { web: 'Web', ios: 'iOS', android: 'Android' };
export const formatPlatform = (platform: string | null | undefined) =>
  platform ? PLATFORM_LABEL[platform] ?? platform : '—';
